import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp, EyeOff, GripVertical, ImagePlus, Instagram, LogOut, Shuffle, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { thumbUrl } from "@/lib/images";
import { captionFor, captionPreview, nameFor, ortFor } from "@/lib/caption";

const STATUS_LABELS = { neu: "Neu", in_bearbeitung: "In Bearbeitung", erledigt: "Erledigt" };
const STATUS_COLORS = {
  neu: "border-ember/60 text-ember",
  in_bearbeitung: "border-bone/40 text-bone/80",
  erledigt: "border-bone/20 text-smoke",
};
const FILTERS = ["alle", "neu", "in_bearbeitung", "erledigt"];

const inputClass =
  "w-full border-b border-black/20 bg-transparent py-2 font-sans text-sm font-normal normal-case tracking-normal text-bone transition-colors focus:border-ember-deep focus:outline-none placeholder:text-smoke/40";

const labelClass = "mb-2 block font-sans text-[13px] font-medium text-bone/90";

const hintClass = "mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke/70";

const RUBRIKEN = [
  ["", "Keine Rubrik"],
  ["dining", "Restaurants"],
  ["hotel", "Hotels"],
  ["reise", "Reisen"],
];
const RUBRIK_LABEL = { "": "—", dining: "Restaurants", hotel: "Hotels", reise: "Reisen" };

const TABS = [
  ["projekte", "Projekte"],
  ["startseite", "Startseite"],
  ["fieldnotes", "Field Notes"],
  ["anfragen", "Anfragen"],
  ["stimmen", "Stimmen"],
  ["einstellungen", "Einstellungen"],
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("alle");
  const [tab, setTab] = useState("projekte");

  const [works, setWorks] = useState([]);
  const [projectMeta, setProjectMeta] = useState({});
  const [files, setFiles] = useState([]);
  const [wClient, setWClient] = useState("");
  const [pwDrafts, setPwDrafts] = useState({});
  const [uploading, setUploading] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [compactWorks, setCompactWorks] = useState(
    () => localStorage.getItem("wj_admin_compact") === "1"
  );
  const [uploadOpen, setUploadOpen] = useState(
    () => localStorage.getItem("wj_admin_upload") === "1"
  );
  const [projectsOrder, setProjectsOrder] = useState([]);
  const [dragProject, setDragProject] = useState(null);
  const fileRef = useRef(null);

  const [notes, setNotes] = useState([]);
  const [nText, setNText] = useState("");
  const [nTextEn, setNTextEn] = useState("");
  const [nLocation, setNLocation] = useState("");
  const [nLocationEn, setNLocationEn] = useState("");
  const [nAlt, setNAlt] = useState("");
  const [nAltEn, setNAltEn] = useState("");
  const [stimmen, setStimmen] = useState([]);
  const [sEditId, setSEditId] = useState(null);
  const [sText, setSText] = useState("");
  const [sTextEn, setSTextEn] = useState("");
  const [sName, setSName] = useState("");
  const [sRole, setSRole] = useState("");
  const [sHouse, setSHouse] = useState("");
  const [storyFor, setStoryFor] = useState(null);
  const [qText, setQText] = useState("");
  const [qTextEn, setQTextEn] = useState("");
  const [qBusy, setQBusy] = useState(false);
  const [qPublish, setQPublish] = useState(false);
  const [hlText, setHlText] = useState("");
  const [hlBusy, setHlBusy] = useState(false);
  const [nDate, setNDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [nFiles, setNFiles] = useState([]);
  const [nSaving, setNSaving] = useState(false);
  const [nEditId, setNEditId] = useState(null);
  const noteFileRef = useRef(null);
  const [wKategorie, setWKategorie] = useState("");
  const [wOrt, setWOrt] = useState("");
  const [wOrtEn, setWOrtEn] = useState("");
  const [wNameEn, setWNameEn] = useState("");
  const [fileProgress, setFileProgress] = useState({}); // { [File]: {pct, status, msg} }
  const [uploadAborted, setUploadAborted] = useState(false);
  const xhrMapRef = useRef(new Map());
  const abortRef = useRef(false);

  const updateFileProgress = (file, pct, status, msg) =>
    setFileProgress((prev) => ({ ...prev, [file]: { pct, status, msg } }));

  const [portrait, setPortrait] = useState(null);
  const [pFile, setPFile] = useState(null);
  const [pUploading, setPUploading] = useState(false);
  const portraitFileRef = useRef(null);
  const [collab, setCollab] = useState(null);
  const [collabSaving, setCollabSaving] = useState(false);
  const [openDetails, setOpenDetails] = useState(null);
  const [editWork, setEditWork] = useState(null);

  const slugify = (name) =>
    name
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/\b[ld]'/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const load = useCallback(() => {
    api
      .get("/admin/works")
      .then((res) => setWorks(res.data))
      .catch(() => setWorks([]));
    api
      .get("/admin/projects")
      .then((res) => {
        setProjectsOrder(res.data.map((p) => p.name));
        setProjectMeta(
          Object.fromEntries(
            res.data.map((p) => [
              p.name,
              {
                art: p.art || "",
                status: p.status || "published",
                name_en: p.name_en || "",
                ort: p.ort || "",
                ort_en: p.ort_en || "",
                context_de: p.context_de || "",
                context_en: p.context_en || "",
                intro_de: p.intro_de || "",
                intro_en: p.intro_en || "",
                show_ort: Boolean(p.show_ort),
                show_home: Boolean(p.show_home),
                home_order: p.home_order || 99,
                protected: Boolean(p.protected),
              },
            ])
          )
        );
      })
      .catch(() => setProjectMeta({}));
    api
      .get("/fieldnotes")
      .then((res) => setNotes(res.data))
      .catch(() => setNotes([]));
    api
      .get("/testimonials")
      .then((res) => setStimmen(res.data))
      .catch(() => setStimmen([]));
    api
      .get("/settings")
      .then((res) => {
        setPortrait(res.data.portrait);
        setCollab(res.data.collab || null);
      })
      .catch(() => {
        setPortrait(null);
        setCollab(null);
      });
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  // Warnung beim Verlassen/Neuladen während eines Uploads
  useEffect(() => {
    if (!uploading) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [uploading]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success("Status aktualisiert.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Anfrage wirklich löschen?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      toast.success("Anfrage gelöscht.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Einzelnes Bild hochladen mit echtem Fortschritt (XMLHttpRequest).
  // onProgress: 0..1 für die Datenübertragung; danach Status "verarbeiten" bis Antwort.
  const uploadOneFile = (file, shared) =>
    new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      const fd = new FormData();
      fd.append("file", file);
      fd.append("year", "0");
      fd.append("art", shared.art);
      fd.append("name", shared.name);
      fd.append("name_en", shared.name_en);
      fd.append("ort", shared.ort);
      fd.append("ort_en", shared.ort_en);
      fd.append("project", shared.name); // Rückwärtskompatibel: Projekt = Name
      fd.append("client_name", shared.name);
      fd.append("featured", "false");
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) shared.onProgress(file, ev.loaded / ev.total, "hochladen");
      };
      xhr.upload.onload = () => shared.onProgress(file, 1, "verarbeiten");
      xhr.onload = () => {
        try {
          const res = JSON.parse(xhr.responseText || "{}");
          if (xhr.status >= 200 && xhr.status < 300) {
            shared.onProgress(file, 1, "fertig");
            resolve({ ok: true, doc: res });
          } else {
            shared.onProgress(file, 1, "fehler", res.detail || "Fehler beim Hochladen");
            resolve({ ok: false, error: res.detail });
          }
        } catch {
          shared.onProgress(file, 1, "fehler", "Unerwartete Antwort");
          resolve({ ok: false, error: "parse" });
        }
      };
      xhr.onerror = () => {
        shared.onProgress(file, 1, "fehler", "Netzwerkfehler");
        resolve({ ok: false, error: "network" });
      };
      xhr.onabort = () => {
        shared.onProgress(file, 0, "wartet");
        resolve({ ok: false, aborted: true });
      };
      shared.xhrMap.set(file, xhr);
      xhr.open("POST", `${api.defaults.baseURL}/admin/works`);
      xhr.withCredentials = true;
      xhr.send(fd);
    });

  const uploadWork = async (e) => {
    e.preventDefault();
    if (files.length === 0 || !wKategorie || !wClient.trim()) {
      toast.error("Bitte Bilder, Kategorie und Name angeben.");
      return;
    }
    setUploading(true);
    setUploadAborted(false);
    const shared = {
      art: wKategorie,
      name: wClient.trim(),
      name_en: wNameEn.trim(),
      ort: wOrt.trim(),
      ort_en: wOrtEn.trim(),
      onProgress: updateFileProgress,
      xhrMap: xhrMapRef.current,
    };
    const results = [];
    const queue = [...files];
    const workers = Array.from({ length: Math.min(3, queue.length) }, async () => {
      while (queue.length && !abortRef.current) {
        const f = queue.shift();
        if (!f) break;
        results.push(await uploadOneFile(f, shared));
      }
    });
    await Promise.all(workers);
    const okDocs = results.filter((r) => r.ok).map((r) => r.doc);
    const failCount = results.filter((r) => !r.ok && !r.aborted).length;
    setWorks((prev) => [...prev, ...okDocs]);
    setUploading(false);
    if (!uploadAborted) {
      setFiles([]);
      setWClient("");
      setWNameEn("");
      setWKategorie("");
      setWOrt("");
      setWOrtEn("");
      setFileProgress({});
      if (fileRef.current) fileRef.current.value = "";
      toast.success(
        failCount > 0
          ? `${okDocs.length} von ${results.length} Bildern hochgeladen (${failCount} Fehler).`
          : `${okDocs.length} ${okDocs.length === 1 ? "Bild" : "Bilder"} in ${RUBRIK_LABEL[wKategorie]} hochgeladen.`
      );
    } else {
      toast.info(`Abgebrochen. ${okDocs.length} bereits hochgeladen.`);
    }
  };

  const cancelUpload = () => {
    abortRef.current = true;
    setUploadAborted(true);
    xhrMapRef.current.forEach((xhr) => xhr.abort());
    xhrMapRef.current.clear();
    setUploading(false);
    setFiles([]);
    setFileProgress({});
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadPortrait = async (e) => {
    e.preventDefault();
    if (!pFile) {
      toast.error("Bitte zuerst ein Bild wählen.");
      return;
    }
    setPUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pFile);
      const { data } = await api.post("/admin/settings/portrait", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPortrait(data.portrait);
      setPFile(null);
      if (portraitFileRef.current) portraitFileRef.current.value = "";
      toast.success("Portrait aktualisiert.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPUploading(false);
    }
  };

  const removeWork = async (id) => {
    if (!window.confirm("Bild wirklich löschen? Das kann nicht rückgängig gemacht werden.")) return;
    try {
      await api.delete(`/admin/works/${id}`);
      setWorks((prev) => prev.filter((w) => w.id !== id));
      setEditWork((cur) => (cur && cur.id === id ? null : cur));
      toast.success("Arbeit gelöscht.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const renameProject = async (oldName, newName) => {
    const clean = (newName || "").trim();
    if (!clean || clean === oldName) return;
    if (
      !window.confirm(
        `Projekt „${oldName}“ in „${clean}“ umbenennen? Alle Bilder und Einstellungen ziehen mit, die öffentliche Adresse ändert sich.`
      )
    )
      return;
    try {
      await api.post("/admin/projects/rename", { old: oldName, new: clean });
      toast.success("Projekt umbenannt.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const shuffleCategory = async (art) => {
    const items = works.filter((w) => (w.art || "").toLowerCase() === art);
    if (!items.length) return;
    try {
      const ids = items.map((w) => w.id);
      for (let i = ids.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [ids[i], ids[j]] = [ids[j], ids[i]];
      }
      await api.patch("/admin/works-order", { ordered_ids: ids });
      toast.success(`Bilder in ${RUBRIK_LABEL[art]} neu gemischt.`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleHomeTile = async (w) => {
    const v = !w.home_tile;
    setWorks((prev) => prev.map((x) => (x.id === w.id ? { ...x, home_tile: v } : x)));
    try {
      await api.patch(`/admin/works/${w.id}/home-tile`, { home_tile: v });
      toast.success(
        v
          ? "Bild erscheint auf der Startseite (die ersten drei)."
          : "Bild nicht mehr auf der Startseite."
      );
    } catch (err) {
      setWorks((prev) => prev.map((x) => (x.id === w.id ? { ...x, home_tile: !v } : x)));
      toast.error(formatApiError(err));
    }
  };

  const resetNoteForm = () => {
    setNText("");
    setNTextEn("");
    setNLocation("");
    setNLocationEn("");
    setNAlt("");
    setNAltEn("");
    setNDate(new Date().toISOString().slice(0, 10));
    setNFiles([]);
    setNEditId(null);
    if (noteFileRef.current) noteFileRef.current.value = "";
  };

  const saveNote = async (e) => {
    e.preventDefault();
    if (nText.trim().length < 2) {
      toast.error("Text fehlt.");
      return;
    }
    setNSaving(true);
    try {
      if (nEditId) {
        await api.put(`/admin/fieldnotes/${nEditId}`, {
          text: nText.trim(),
          location: nLocation.trim(),
          date: nDate,
          text_en: nTextEn.trim(),
          location_en: nLocationEn.trim(),
          alt_de: nAlt.trim(),
          alt_en: nAltEn.trim(),
        });
        toast.success("Notiz aktualisiert.");
      } else {
        const fd = new FormData();
        fd.append("text", nText.trim());
        fd.append("date", nDate);
        fd.append("location", nLocation.trim());
        fd.append("text_en", nTextEn.trim());
        fd.append("location_en", nLocationEn.trim());
        fd.append("alt_de", nAlt.trim());
        fd.append("alt_en", nAltEn.trim());
        nFiles.slice(0, 3).forEach((f) => fd.append("files", f));
        await api.post("/admin/fieldnotes", fd);
        toast.success("Notiz gespeichert.");
      }
      resetNoteForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setNSaving(false);
    }
  };

  const startEditNote = (note) => {
    setNEditId(note.id);
    setNText(note.text);
    setNTextEn(note.text_en || "");
    setNLocation(note.location || "");
    setNLocationEn(note.location_en || "");
    setNAlt(note.alt_de || "");
    setNAltEn(note.alt_en || "");
    setNDate(note.date?.slice(0, 10) || new Date().toISOString().slice(0, 10));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetStimmeForm = () => {
    setSEditId(null);
    setSText("");
    setSTextEn("");
    setSName("");
    setSRole("");
    setSHouse("");
  };

  const saveStimme = async (e) => {
    e.preventDefault();
    try {
      const body = {
        text_de: sText.trim(),
        text_en: sTextEn.trim(),
        name: sName.trim(),
        role: sRole.trim(),
        house: sHouse.trim(),
        order: 0,
      };
      if (sEditId) {
        await api.put(`/admin/testimonials/${sEditId}`, body);
      } else {
        await api.post("/admin/testimonials", body);
      }
      resetStimmeForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startEditStimme = (q) => {
    setSEditId(q.id);
    setSText(q.text_de || "");
    setSTextEn(q.text_en || "");
    setSName(q.name || "");
    setSRole(q.role || "");
    setSHouse(q.house || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteStimme = async (id) => {
    if (!window.confirm("Kundenstimme wirklich löschen?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    load();
  };

  const makeStoryFor = async (note) => {
    setStoryFor(note.id);
    try {
      const { data } = await api.post(`/admin/fieldnotes/${note.id}/story`);
      data.stories.forEach((u) => window.open(`${u}?dl=1`, "_blank"));
      toast.success(
        data.stories.length > 1
          ? `${data.stories.length} Story-Slides erstellt, Downloads gestartet.`
          : "Story-Slide erstellt, Download gestartet."
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setStoryFor(null);
    }
  };

  const makeQuoteStory = async () => {
    if (!qText.trim()) {
      toast.error("Bitte zuerst einen Gedanken eintippen.");
      return;
    }
    setQBusy(true);
    try {
      const { data } = await api.post("/admin/story/quote", {
        text: qText.trim(),
        text_en: qTextEn.trim(),
      });
      window.open(`${data.story}?dl=1`, "_blank");
      if (data.story_en) window.open(`${data.story_en}?dl=1`, "_blank");
      if (qPublish) {
        const fd = new FormData();
        fd.append("text", qText.trim());
        fd.append("date", new Date().toISOString().slice(0, 10));
        if (qTextEn.trim()) fd.append("text_en", qTextEn.trim());
        await api.post("/admin/fieldnotes", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const res = await api.get("/fieldnotes");
        setNotes(res.data);
        setQText("");
        setQTextEn("");
        toast.success(
          data.story_en
            ? "Stories (DE + EN) erstellt und als Fieldnote veröffentlicht."
            : "Story erstellt und als Fieldnote veröffentlicht."
        );
      } else {
        toast.success(
          data.story_en
            ? "Zitat-Stories (DE + EN) erstellt, Downloads gestartet."
            : "Zitat-Story erstellt, Download gestartet."
        );
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setQBusy(false);
    }
  };

  const makeHighlight = async () => {
    if (!hlText.trim()) {
      toast.error("Bitte zuerst ein Wort eintippen.");
      return;
    }
    setHlBusy(true);
    try {
      const { data } = await api.post("/admin/story/highlight", { label: hlText.trim() });
      window.open(`${data.cover}?dl=1`, "_blank");
      toast.success("Highlight-Cover erstellt, Download gestartet.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setHlBusy(false);
    }
  };

  const removeNote = async (id) => {
    try {
      await api.delete(`/admin/fieldnotes/${id}`);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      toast.success("Notiz gelöscht.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addNoteImages = async (fileList) => {
    const chosen = Array.from(fileList || []).slice(0, 3);
    if (!nEditId || chosen.length === 0) return;
    const fd = new FormData();
    chosen.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/admin/fieldnotes/${nEditId}/images`, fd);
      setNotes((prev) => prev.map((n) => (n.id === nEditId ? { ...n, images: data.images } : n)));
      toast.success("Bild hinzugefügt.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeNoteImage = async (idx) => {
    if (!nEditId) return;
    try {
      const { data } = await api.delete(`/admin/fieldnotes/${nEditId}/images/${idx}`);
      setNotes((prev) => prev.map((n) => (n.id === nEditId ? { ...n, images: data.images } : n)));
      toast.success("Bild entfernt.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveWorkMeta = async (id, fields) => {
    const current = works.find((w) => w.id === id);
    if (!current) return;
    const merged = {
      title: (fields.title ?? current.title ?? "").trim(),
      client_name: (fields.client_name ?? current.client_name ?? "").trim(),
      alt_de: (fields.alt_de ?? current.alt_de ?? "").trim(),
      alt_en: (fields.alt_en ?? current.alt_en ?? "").trim(),
    };
    if (fields.art !== undefined) merged.art = fields.art;
    if (fields.hidden !== undefined) merged.hidden = fields.hidden;
    if (fields.project !== undefined) merged.project = fields.project.trim();
    if (fields.ort !== undefined) merged.ort = fields.ort.trim();
    if (fields.ort_en !== undefined) merged.ort_en = fields.ort_en.trim();
    if (fields.name !== undefined) merged.name = fields.name.trim();
    if (fields.name_en !== undefined) merged.name_en = fields.name_en.trim();
    if (fields.description !== undefined) merged.description = fields.description.trim();
    if (fields.description_en !== undefined) merged.description_en = fields.description_en.trim();
    try {
      await api.patch(`/admin/works/${id}`, merged);
      setWorks((prev) => prev.map((w) => (w.id === id ? { ...w, ...merged } : w)));
      setEditWork((cur) => (cur && cur.id === id ? { ...cur, ...merged } : cur));
      toast.success("Gespeichert.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const assignProject = async (id, project) => {
    const current = works.find((w) => w.id === id)?.project || "";
    if (project.trim() === current) return;
    try {
      await api.patch(`/admin/works/${id}/project`, { project: project.trim() });
      setWorks((prev) =>
        prev.map((w) => (w.id === id ? { ...w, project: project.trim() } : w))
      );
      toast.success(project.trim() ? "Projekt zugewiesen." : "Projekt entfernt.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveProjectMeta = async (project, fields) => {
    const payload = { project };
    Object.entries(fields).forEach(([k, v]) => {
      payload[k] = typeof v === "string" ? v.trim() : v;
    });
    try {
      await api.put("/admin/projects/meta", payload);
      setProjectMeta((prev) => {
        const cur = prev[project] || {};
        const next = { ...cur };
        Object.entries(fields).forEach(([k, v]) => {
          next[k] = typeof v === "string" ? v.trim() : v;
        });
        if (fields.password !== undefined) {
          next.protected = Boolean(String(fields.password).trim());
          delete next.password;
        }
        return { ...prev, [project]: next };
      });
      toast.success("Gespeichert.");
      if (fields.status === "published") {
        toast.info(
          "Hinweis: Die statische SEO-Version (Google, Link-Vorschauen, Sitemap) entsteht beim nächsten Redeploy. Sag mir einfach Bescheid, dann stoße ich ihn an.",
          { duration: 9000 }
        );
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveCollab = async () => {
    setCollabSaving(true);
    try {
      await api.put("/admin/settings/collab", { blocks: collab });
      toast.success("Startseiten-Texte gespeichert.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCollabSaving(false);
    }
  };

  const moveWork = async (fromId, toId) => {
    if (!fromId || fromId === toId) return;
    const arr = [...works];
    const from = arr.findIndex((w) => w.id === fromId);
    const to = arr.findIndex((w) => w.id === toId);
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setWorks(arr);
    setDragId(null);
    try {
      await api.patch("/admin/works-order", { ordered_ids: arr.map((w) => w.id) });
      toast.success("Reihenfolge gespeichert.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Übersicht nach Kategorien (dining/hotel/reise), Bilder ohne Kategorie unter „ohne“
  const groupedWorks = (() => {
    const groups = new Map([["dining", []], ["hotel", []], ["reise", []], ["", []]]);
    works.forEach((w) => {
      const key = (w.art || "").toLowerCase();
      const k = groups.has(key) ? key : "";
      groups.get(k).push(w);
    });
    groups.forEach((items) => items.sort((a, b) => (a.order || 0) - (b.order || 0)));
    return [...groups.entries()].filter(([, items]) => items.length > 0);
  })();

  const moveProject = async (fromName, toName) => {
    if (!fromName || fromName === toName) return;
    const current = groupedWorks.map(([p]) => p).filter(Boolean);
    const from = current.indexOf(fromName);
    const to = current.indexOf(toName);
    if (from === -1 || to === -1) return;
    const arr = [...current];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setProjectsOrder(arr);
    setDragProject(null);
    try {
      await api.patch("/admin/projects-order", { ordered_projects: arr });
      toast.success("Reihenfolge der Projekte gespeichert.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const dropOnWork = (targetId, targetArt) => {
    const dragged = works.find((w) => w.id === dragId);
    if (!dragged) return;
    if ((dragged.art || "").toLowerCase() !== targetArt) {
      setDragId(null);
      toast.error("Verschiedene Kategorien: Öffne das Bild, um die Kategorie zu ändern.");
      return;
    }
    moveWork(dragId, targetId);
  };

  const projectCover = (items) => items.find((w) => !w.hidden)?.image || items[0]?.image;

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="admin-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-smoke">Laden …</p>
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6" data-testid="admin-login-view">
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          onSubmit={login}
          className="w-full max-w-md border border-black/10 p-10"
          data-testid="admin-login-form"
        >
          <p className="mb-3 font-mono text-sm uppercase tracking-[0.15em] text-smoke">Admin Bereich</p>
          <h1 className="mb-8 font-serif text-3xl text-bone">Anmelden</h1>
          <div className="space-y-5">
            <div>
              <label htmlFor="admin-email" className={labelClass}>
                E-Mail
              </label>
              <input
                id="admin-email"
                type="email"
                autoComplete="email"
                value={email}
                data-testid="admin-email-input"
                required
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="admin-password" className={labelClass}>
                Passwort
              </label>
              <input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                data-testid="admin-password-input"
                required
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          {error && (
            <p className="mt-4 font-sans text-sm text-ember" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-8 w-full border border-ember-deep bg-ember-deep px-8 py-3.5 font-sans text-sm font-medium tracking-wide text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-ember disabled:opacity-50"
          >
            {loading ? "Wird geprüft …" : "Anmelden"}
          </button>
        </motion.form>
      </div>
    );
  }

  const filtered = filter === "alle" ? inquiries : inquiries.filter((i) => i.status === filter);
  const homeTileCount = works.filter((w) => w.home_tile).length;
  const tabLabel = TABS.find(([k]) => k === tab)?.[1] || "";

  return (
    <div className="mx-auto max-w-7xl px-6 pb-24 pt-10 md:px-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-sm uppercase tracking-[0.15em] text-smoke">Admin</p>
          <h1 className="mt-1 font-serif text-4xl text-bone md:text-5xl" data-testid="admin-heading">
            {tabLabel}
            {tab === "anfragen" && (
              <span className="ml-3 font-mono text-2xl text-smoke">({inquiries.length})</span>
            )}
            {tab === "projekte" && (
              <span className="ml-3 font-mono text-2xl text-smoke">
                ({groupedWorks.filter(([p]) => p).length})
              </span>
            )}
          </h1>
        </div>
        <button
          type="button"
          data-testid="admin-logout-btn"
          onClick={logout}
          className="inline-flex items-center gap-2 border border-black/20 px-5 py-3 font-sans text-[13px] font-medium text-smoke transition-colors hover:border-ember hover:text-ember-deep"
        >
          Abmelden <LogOut size={13} />
        </button>
      </div>

      <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 border-y border-black/10 py-5" data-testid="admin-tabs">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`font-sans text-[13px] font-medium tracking-wide transition-colors ${
              tab === key ? "text-ember" : "text-smoke hover:text-bone"
            }`}
          >
            {label}
            {key === "anfragen" && <span className="ml-2 text-bone/40">{inquiries.length}</span>}
            {key === "fieldnotes" && <span className="ml-2 text-bone/40">{notes.length}</span>}
          </button>
        ))}
      </div>

      {/* ============ PROJEKTE ============ */}
      {tab === "projekte" && (
        <div className="mt-10" data-testid="admin-projects-section">
          <p className="mb-8 max-w-2xl font-sans text-sm font-light leading-relaxed text-smoke">
            Jedes Projekt ist eine Bildserie. Klicke ein Projekt an, um seine Einstellungen und
            Bilder zu bearbeiten. Mit „Mischen“ und „Kompakt“ sortierst du die Bilder schnell neu.
          </p>

          <button
            type="button"
            data-testid="upload-form-toggle"
            onClick={() =>
              setUploadOpen((v) => {
                const n = !v;
                localStorage.setItem("wj_admin_upload", n ? "1" : "0");
                return n;
              })
            }
            className="flex w-full items-center justify-between border border-black/10 px-6 py-4 font-sans text-[13px] font-medium tracking-wide text-smoke transition-colors hover:border-ember hover:text-ember-deep md:px-8"
          >
            <span className="flex items-center gap-3">
              <ImagePlus size={15} /> Neue Bilder hochladen
            </span>
            {uploadOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
          {uploadOpen && (
            <form onSubmit={uploadWork} className="mt-4 border border-black/10 p-6 md:p-8" data-testid="work-upload-form">
              {/* Bilder wählen */}
              <label
                htmlFor="work-file"
                data-testid="work-file-label"
                className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-black/20 px-6 py-8 font-sans text-[13px] font-medium text-smoke transition-colors hover:border-ember hover:text-ember-deep"
              >
                <ImagePlus size={16} />
                {files.length > 0 ? `${files.length} Bild${files.length > 1 ? "er" : ""} gewählt` : "Bilder wählen (JPG, PNG, WebP, auch mehrere)"}
              </label>
              <input
                id="work-file"
                ref={fileRef}
                data-testid="work-file-input"
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const list = Array.from(e.target.files || []).slice(0, 20);
                  setFiles(list);
                  setFileProgress(Object.fromEntries(list.map((f) => [f, { pct: 0, status: "wartet" }])));
                }}
              />

              {/* Kategorie: drei große Knöpfe */}
              <div className="mt-8">
                <p className={labelClass}>Kategorie *</p>
                <div className="flex flex-wrap gap-3" data-testid="work-kategorie-buttons">
                  {RUBRIKEN.filter(([v]) => v).map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`work-kat-${v}`}
                      onClick={() => setWKategorie(v)}
                      className={`border px-6 py-3 font-sans text-sm font-medium transition-colors ${
                        wKategorie === v
                          ? "border-ember-deep bg-ember-deep text-obsidian"
                          : "border-black/20 text-bone hover:border-ember hover:text-ember-deep"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name / Ort */}
              <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="work-client" className={labelClass}>
                    Name *
                  </label>
                  <input
                    id="work-client"
                    data-testid="work-client-input"
                    value={wClient}
                    onChange={(e) => setWClient(e.target.value)}
                    placeholder="z. B. Esperit Roca"
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="work-name-en" className={labelClass}>
                    Name auf Englisch
                  </label>
                  <input
                    id="work-name-en"
                    data-testid="work-name-en-input"
                    value={wNameEn}
                    onChange={(e) => setWNameEn(e.target.value)}
                    placeholder="nur falls anders"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="work-ort" className={labelClass}>
                    Ort
                  </label>
                  <input
                    id="work-ort"
                    data-testid="work-ort-input"
                    value={wOrt}
                    onChange={(e) => setWOrt(e.target.value)}
                    placeholder="z. B. Girona"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="work-ort-en" className={labelClass}>
                    Ort auf Englisch
                  </label>
                  <input
                    id="work-ort-en"
                    data-testid="work-ort-en-input"
                    value={wOrtEn}
                    onChange={(e) => setWOrtEn(e.target.value)}
                    placeholder="nur falls anders, z. B. Brittany"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* So erscheint es */}
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke" data-testid="work-preview-line">
                So erscheint es:{" "}
                <span className="text-bone">
                  {captionPreview({ name: wClient, name_en: wNameEn, ort: wOrt, ort_en: wOrtEn })}
                </span>
              </p>

              {/* Buttons */}
              <div className="mt-8 flex items-center gap-4">
                <button
                  type="submit"
                  data-testid="work-upload-submit"
                  disabled={uploading || files.length === 0 || !wKategorie || !wClient.trim()}
                  className="inline-flex items-center gap-3 border border-ember-deep bg-ember-deep px-8 py-3.5 font-sans text-sm font-medium text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-ember disabled:opacity-50"
                >
                  {uploading ? "Lädt hoch …" : "Hochladen"}
                </button>
                <button
                  type="button"
                  data-testid="work-upload-cancel"
                  onClick={cancelUpload}
                  className="font-sans text-sm font-medium text-smoke transition-colors hover:text-ember-deep"
                >
                  Abbrechen
                </button>
              </div>

              {/* Gesamt-Fortschritt */}
              {uploading && (
                <div className="mt-8" data-testid="upload-progress-overall">
                  <div className="h-1.5 w-full bg-black/10">
                    <div
                      className="h-1.5 bg-ember transition-all duration-200"
                      style={{
                        width: `${Math.round(
                          (Object.values(fileProgress).reduce((s, p) => s + (p.pct || 0), 0) /
                            Math.max(1, files.length)) *
                            100
                        )}%`,
                      }}
                    />
                  </div>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                    {Object.values(fileProgress).filter((p) => p.status === "fertig").length} von {files.length} Bildern hochgeladen ·{" "}
                    {Math.round(
                      (Object.values(fileProgress).reduce((s, p) => s + (p.pct || 0), 0) / Math.max(1, files.length)) * 100
                    )}
                    %
                  </p>
                </div>
              )}

              {/* Liste der Bilder */}
              {files.length > 0 && (
                <div className="mt-6 space-y-3" data-testid="upload-file-list">
                  {files.map((f, i) => {
                    const p = fileProgress[f] || { pct: 0, status: "wartet" };
                    const url = URL.createObjectURL(f);
                    return (
                      <div key={i} className="flex items-center gap-4 border border-black/10 p-3" data-testid={`upload-row-${i}`}>
                        <img src={url} alt="" className="h-12 w-12 object-cover" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-sans text-[13px] text-bone">{f.name}</p>
                          <div className="mt-1.5 h-1 w-full bg-black/10">
                            <div
                              className={`h-1 transition-all duration-200 ${p.status === "fehler" ? "bg-red-500" : "bg-ember"}`}
                              style={{ width: `${Math.round((p.pct || 0) * 100)}%` }}
                            />
                          </div>
                        </div>
                        <p className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] ${p.status === "fehler" ? "text-red-400" : "text-smoke"}`}>
                          {p.status === "fertig" ? "Fertig" : p.status === "verarbeiten" ? "Wird verarbeitet" : p.status === "fehler" ? (p.msg || "Fehler") : p.pct > 0 ? "Lädt hoch" : "Wartet"}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </form>
          )}

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
              <GripVertical size={13} /> Bilder am Griff ziehen, um die Reihenfolge zu ändern
            </p>
          </div>

          {/* Übersicht nach Kategorien */}
          <div className="mt-4" data-testid="admin-projects-list">
            {groupedWorks.map(([art, items]) => {
              const meta = projectMeta[art] || {};
              return (
                <div key={art} className="mt-6 border border-black/10" data-testid={`project-row-${art || "ohne"}`}>
                  <div className="flex flex-wrap items-center gap-4 px-5 py-4 md:px-6">
                    <div className="min-w-0 flex-1">
                      <p className="font-serif text-lg text-bone">{RUBRIK_LABEL[art] || "Ohne Kategorie"}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                        {items.length} Bild{items.length > 1 ? "er" : ""}
                        {meta.status === "draft" && <span className="text-ember"> · Entwurf</span>}
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid={`project-open-${art || "ohne"}`}
                      onClick={() => setOpenDetails(openDetails === art ? null : art)}
                      className="border border-ember-deep bg-ember-deep px-4 py-2 font-sans text-[12px] font-medium text-obsidian transition-colors hover:bg-transparent hover:text-ember"
                    >
                      {openDetails === art ? "Schließen" : "Öffnen"}
                    </button>
                  </div>

                  {openDetails === art && (
                    <div className="border-t border-black/10 px-5 py-6 md:px-8" data-testid={`project-details-${art || "ohne"}`}>
                      {/* Einstellungen der Kategorie */}
                      <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-smoke/60">
                        Einstellungen
                      </p>
                      <div className="mb-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
                        <div>
                          <label className={labelClass}>Status</label>
                          <select
                            value={meta.status || "published"}
                            onChange={(e) => saveProjectMeta(art, { status: e.target.value })}
                            data-testid={`project-status-${art || "ohne"}`}
                            className={inputClass}
                          >
                            <option value="published">Veröffentlicht</option>
                            <option value="draft">Entwurf</option>
                          </select>
                        </div>
                        <label className="flex cursor-pointer items-center gap-2 pb-2 font-sans text-[13px] font-medium text-smoke">
                          <input
                            type="checkbox"
                            data-testid={`project-show-home-${art || "ohne"}`}
                            checked={Boolean(meta.show_home)}
                            onChange={(e) => {
                              const v = e.target.checked;
                              setProjectMeta((prev) => ({ ...prev, [art]: { ...(prev[art] || {}), show_home: v } }));
                              saveProjectMeta(art, { show_home: v });
                            }}
                            className="h-4 w-4 accent-[#B5401F]"
                          />
                          Als Kachel auf der Startseite
                        </label>
                      </div>

                      {/* Texte */}
                      <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-smoke/60">
                        Texte
                      </p>
                      <div className="mb-8 grid grid-cols-1 gap-5 md:grid-cols-2">
                        {[
                          ["intro_de", "Einleitungstext (DE), zwei bis drei Sätze"],
                          ["intro_en", "Einleitungstext (EN), two to three sentences"],
                        ].map(([field, label]) => (
                          <div key={field} className="md:col-span-2">
                            <label className={labelClass}>{label}</label>
                            <textarea
                              value={meta[field] || ""}
                              data-testid={`project-${field}-${art || "ohne"}`}
                              rows={3}
                              onChange={(e) =>
                                setProjectMeta((prev) => ({
                                  ...prev,
                                  [art]: { ...(prev[art] || {}), [field]: e.target.value },
                                }))
                              }
                              onBlur={(e) => saveProjectMeta(art, { [field]: e.currentTarget.value })}
                              className={`${inputClass} resize-y`}
                            />
                          </div>
                        ))}
                      </div>

                      {/* Passwort */}
                      <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-smoke/60">
                        Passwort
                      </p>
                      <div data-testid={`project-password-row-${art || "ohne"}`} className="mb-8">
                        <div className="flex flex-wrap items-center gap-3">
                          <input
                            type="text"
                            data-testid={`project-password-input-${art || "ohne"}`}
                            value={pwDrafts[art] || ""}
                            onChange={(e) => setPwDrafts((prev) => ({ ...prev, [art]: e.target.value }))}
                            placeholder={meta.protected ? "Passwort gesetzt, hier ändern" : "nicht gesetzt (öffentlich)"}
                            className={`${inputClass} max-w-xs`}
                          />
                          <button
                            type="button"
                            data-testid={`project-password-save-${art || "ohne"}`}
                            onClick={() => {
                              const v = (pwDrafts[art] || "").trim();
                              if (v) {
                                saveProjectMeta(art, { password: v });
                                setPwDrafts((prev) => ({ ...prev, [art]: "" }));
                              }
                            }}
                            className="font-sans text-[12px] font-medium text-smoke transition-colors hover:text-ember-deep"
                          >
                            {meta.protected ? "Ändern" : "Setzen"}
                          </button>
                          {meta.protected && (
                            <button
                              type="button"
                              data-testid={`project-password-remove-${art || "ohne"}`}
                              onClick={() => saveProjectMeta(art, { password: "" })}
                              className="font-sans text-[12px] font-medium text-smoke transition-colors hover:text-ember-deep"
                            >
                              Entfernen
                            </button>
                          )}
                        </div>
                        <p className={hintClass}>
                          Gilt für die ganze Kategorie. Besucher sehen die Bilder erst nach Eingabe.
                        </p>
                      </div>

                      {/* Bilder */}
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke/60">
                          Bilder — am Griff ziehen, um die Reihenfolge zu ändern
                        </p>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            data-testid={`works-shuffle-${art || "ohne"}`}
                            onClick={() => shuffleCategory(art)}
                            className="flex items-center gap-2 border border-black/20 px-4 py-2 font-sans text-[12px] font-medium text-smoke transition-colors hover:border-ember hover:text-ember-deep"
                          >
                            <Shuffle size={13} /> Mischen
                          </button>
                          <button
                            type="button"
                            data-testid="works-compact-toggle"
                            onClick={() => setCompactWorks((v) => { const n = !v; localStorage.setItem("wj_admin_compact", n ? "1" : "0"); return n; })}
                            className={`border px-4 py-2 font-sans text-[12px] font-medium transition-colors ${
                              compactWorks ? "border-ember-deep bg-ember-deep text-obsidian" : "border-black/20 text-smoke hover:border-ember hover:text-ember-deep"
                            }`}
                          >
                            {compactWorks ? "Zurück" : "Kompakt"}
                          </button>
                        </div>
                      </div>
                      <div
                        className={`mt-4 ${
                          compactWorks ? "grid grid-cols-3 gap-2 sm:grid-cols-5 md:grid-cols-8" : "grid grid-cols-2 gap-4 md:grid-cols-4"
                        }`}
                      >
                        {items.map((w, wIdx) => (
                          <div
                            key={w.id}
                            className={`group relative cursor-grab border active:cursor-grabbing ${dragId === w.id ? "border-ember" : "border-black/10"}`}
                            data-testid={`admin-work-${w.id}`}
                            draggable
                            onDragStart={() => setDragId(w.id)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => dropOnWork(w.id, art)}
                            title={captionFor(w)}
                          >
                            <img src={compactWorks ? thumbUrl(w.image, 300) : w.image} alt={captionFor(w)} loading="lazy" className="aspect-square w-full object-cover" />
                            <button type="button" onClick={() => setEditWork(w)} data-testid={`work-open-${w.id}`} aria-label="Bild bearbeiten" className="absolute inset-0 cursor-pointer">
                              <span className="sr-only">Bild bearbeiten</span>
                            </button>
                            {compactWorks ? (
                              <span className="pointer-events-none absolute left-1 top-1 bg-obsidian/80 px-1.5 py-0.5 font-mono text-[9px] tracking-[0.1em] text-bone" data-testid={`admin-work-pos-${w.id}`}>
                                {wIdx + 1}
                              </span>
                            ) : (
                              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-obsidian/70 p-2.5 backdrop-blur-sm">
                                <p className="min-w-0 flex-1 truncate font-sans text-[13px] text-bone" title={captionFor(w)}>
                                  {captionFor(w)}
                                </p>
                                <div className="flex shrink-0 items-center gap-1">
                                  <button
                                    type="button"
                                    data-testid={`work-hide-${w.id}`}
                                    onClick={(e) => { e.stopPropagation(); saveWorkMeta(w.id, { hidden: !w.hidden }); }}
                                    aria-label={w.hidden ? "Bild einblenden" : "Bild verstecken"}
                                    className={`pointer-events-auto p-1.5 transition-colors ${w.hidden ? "text-ember" : "text-smoke hover:text-ember-deep"}`}
                                  >
                                    <EyeOff size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    data-testid={`work-delete-${w.id}`}
                                    onClick={(e) => { e.stopPropagation(); removeWork(w.id); }}
                                    aria-label="Arbeit löschen"
                                    className="pointer-events-auto p-1.5 text-smoke transition-colors hover:text-ember-deep"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {groupedWorks.length === 0 && (
              <p className="mt-6 border border-black/10 p-10 text-center font-sans text-sm text-smoke">
                Noch keine Bilder. Lade oben deine ersten hoch.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ============ STARTSEITE ============ */}
      {tab === "startseite" && (
        <div className="mt-10" data-testid="admin-startseite-section">
          <p className="mb-8 max-w-2xl font-sans text-sm font-light leading-relaxed text-smoke">
            Wähle hier die drei Kacheln auf der Startseite aus. Klicke ein Bild an, um es zu
            markieren — maximal drei. Die Reihenfolge folgt der Reihenfolge der Projekte und Bilder.
          </p>
          <div className="border border-black/10 p-6 md:p-8">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke/60">
                Startseiten-Kacheln
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke" data-testid="home-tile-count">
                {homeTileCount} von 3 gewählt
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 md:grid-cols-6">
              {works.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => toggleHomeTile(w)}
                  data-testid={`home-tile-${w.id}`}
                  title={w.title}
                  className={`group relative aspect-square overflow-hidden border transition-all ${
                    w.home_tile ? "border-ember ring-2 ring-ember/60" : "border-black/10 hover:border-ember/50"
                  }`}
                >
                  <img src={thumbUrl(w.image, 300)} alt={w.title} loading="lazy" className="h-full w-full object-cover" />
                  {w.home_tile && (
                    <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-ember text-obsidian">
                      ✓
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 border border-black/10 p-6 md:p-8">
            <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.3em] text-ember-deep">
              Zusammenarbeit — drei Textblöcke auf der Startseite
            </p>
            <p className="mb-8 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
              Titel und Text je Sprache. Leer lassen, um den Standard zu nutzen.
            </p>
            {(collab || [
              { title_de: "", text_de: "", title_en: "", text_en: "" },
              { title_de: "", text_de: "", title_en: "", text_en: "" },
              { title_de: "", text_de: "", title_en: "", text_en: "" },
            ]).map((b, i) => (
              <div key={i} className="mb-10 border-b border-black/10 pb-10 last:mb-0 last:border-0 last:pb-0" data-testid={`collab-edit-${i + 1}`}>
                <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
                  Block {i + 1}
                </p>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {[
                    ["title_de", "Titel (DE)", "input"],
                    ["title_en", "Titel (EN)", "input"],
                    ["text_de", "Text (DE)", "textarea"],
                    ["text_en", "Text (EN)", "textarea"],
                  ].map(([field, label, kind]) => (
                    <div key={field}>
                      <label className={labelClass}>{label}</label>
                      {kind === "input" ? (
                        <input
                          value={b[field]}
                          data-testid={`collab-${field}-${i + 1}`}
                          onChange={(e) =>
                            setCollab((prev) => {
                              const base = prev && prev.length === 3 ? prev : [
                                { title_de: "", text_de: "", title_en: "", text_en: "" },
                                { title_de: "", text_de: "", title_en: "", text_en: "" },
                                { title_de: "", text_de: "", title_en: "", text_en: "" },
                              ];
                              const next = base.map((x, j) => (j === i ? { ...x, [field]: e.target.value } : x));
                              return next;
                            })
                          }
                          className={inputClass}
                        />
                      ) : (
                        <textarea
                          value={b[field]}
                          data-testid={`collab-${field}-${i + 1}`}
                          rows={3}
                          onChange={(e) =>
                            setCollab((prev) => {
                              const base = prev && prev.length === 3 ? prev : [
                                { title_de: "", text_de: "", title_en: "", text_en: "" },
                                { title_de: "", text_de: "", title_en: "", text_en: "" },
                                { title_de: "", text_de: "", title_en: "", text_en: "" },
                              ];
                              const next = base.map((x, j) => (j === i ? { ...x, [field]: e.target.value } : x));
                              return next;
                            })
                          }
                          className={`${inputClass} resize-y`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <button
              type="button"
              data-testid="collab-save-btn"
              onClick={saveCollab}
              disabled={collabSaving}
              className="mt-6 inline-flex items-center gap-3 border border-ember-deep bg-ember-deep px-8 py-3.5 font-sans text-sm font-medium text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-ember disabled:opacity-50"
            >
              {collabSaving ? "Wird gespeichert …" : "Speichern"}
            </button>
          </div>
        </div>
      )}

      {/* ============ FIELD NOTES ============ */}
      {tab === "fieldnotes" && (
        <div className="mt-10" data-testid="admin-notes-section">
          <form onSubmit={saveNote} className="border border-black/10 p-6 md:p-8" data-testid="note-form">
            <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-ember-deep">
              {nEditId ? "Notiz bearbeiten" : "Neue Notiz"}
            </p>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="md:col-span-2">
                <label htmlFor="note-text" className={labelClass}>
                  Text *
                </label>
                <textarea
                  id="note-text"
                  data-testid="note-text-input"
                  required
                  minLength={2}
                  maxLength={1000}
                  rows={3}
                  value={nText}
                  onChange={(e) => setNText(e.target.value)}
                  placeholder="z. B. Um 6 Uhr früh in der Küche vom Lakeside — das Licht kam genau richtig."
                  className={`${inputClass} resize-y`}
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="note-text-en" className={labelClass}>
                  Text Englisch (optional)
                </label>
                <textarea
                  id="note-text-en"
                  data-testid="note-text-en-input"
                  maxLength={1000}
                  rows={3}
                  value={nTextEn}
                  onChange={(e) => setNTextEn(e.target.value)}
                  placeholder="English version of the note (shown when visitors switch to EN)"
                  className={`${inputClass} resize-y`}
                />
              </div>
              <div>
                <label htmlFor="note-location" className={labelClass}>
                  Ort (optional)
                </label>
                <input
                  id="note-location"
                  data-testid="note-location-input"
                  value={nLocation}
                  onChange={(e) => setNLocation(e.target.value)}
                  placeholder="z. B. Lakeside, Hamburg"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="note-date" className={labelClass}>
                  Datum *
                </label>
                <input
                  id="note-date"
                  data-testid="note-date-input"
                  type="date"
                  required
                  value={nDate}
                  onChange={(e) => setNDate(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="note-location-en" className={labelClass}>
                  Ort Englisch (optional)
                </label>
                <input
                  id="note-location-en"
                  data-testid="note-location-en-input"
                  value={nLocationEn}
                  onChange={(e) => setNLocationEn(e.target.value)}
                  placeholder="z. B. Brittany"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="note-alt" className={labelClass}>
                  Alt-Text der Bilder (optional)
                </label>
                <input
                  id="note-alt"
                  data-testid="note-alt-input"
                  value={nAlt}
                  onChange={(e) => setNAlt(e.target.value)}
                  placeholder="z. B. Morgenlicht über dem Pass im Lakeside"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="note-alt-en" className={labelClass}>
                  Alt-Text Englisch (optional)
                </label>
                <input
                  id="note-alt-en"
                  data-testid="note-alt-en-input"
                  value={nAltEn}
                  onChange={(e) => setNAltEn(e.target.value)}
                  placeholder="e. g. morning light over the pass at Lakeside"
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-2">
                {nEditId && (
                  <div className="mb-4 flex flex-wrap items-start gap-4" data-testid="note-edit-images">
                    {(notes.find((n) => n.id === nEditId)?.images || []).map((img, i) => (
                      <div key={img.storage_path || i} className="relative">
                        <img
                          src={img.image}
                          alt=""
                          data-testid={`note-edit-image-${i}`}
                          className="h-20 w-auto border border-black/10"
                        />
                        <button
                          type="button"
                          data-testid={`note-edit-image-remove-${i}`}
                          onClick={() => removeNoteImage(i)}
                          aria-label="Bild entfernen"
                          className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-black/20 bg-obsidian text-smoke transition-colors hover:border-ember hover:text-ember-deep"
                        >
                          <X size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {nEditId && (notes.find((n) => n.id === nEditId)?.images || []).length >= 3 ? (
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke" data-testid="note-images-full-hint">
                    Maximal 3 Bilder — erst eines entfernen, um ein neues zu laden.
                  </p>
                ) : (
                  <>
                    <label
                      htmlFor="note-file"
                      data-testid="note-file-label"
                      className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-black/20 px-6 py-6 font-sans text-[13px] font-medium text-smoke transition-colors hover:border-ember hover:text-ember-deep"
                    >
                      <ImagePlus size={16} />
                      {nEditId
                        ? "Bilder ergänzen oder ersetzen (max. 3)"
                        : nFiles.length > 0
                          ? `${nFiles.length} Bild${nFiles.length > 1 ? "er" : ""} gewählt`
                          : "Bilder wählen (optional, max. 3)"}
                    </label>
                    <input
                      id="note-file"
                      ref={noteFileRef}
                      data-testid="note-file-input"
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        if (nEditId) {
                          addNoteImages(e.target.files);
                          e.target.value = "";
                        } else {
                          setNFiles(Array.from(e.target.files || []).slice(0, 3));
                        }
                      }}
                    />
                  </>
                )}
              </div>
              <div className="flex items-end gap-4 md:col-span-2">
                <button
                  type="submit"
                  data-testid="note-submit-btn"
                  disabled={nSaving}
                  className="border border-ember-deep bg-ember-deep px-8 py-3 font-sans text-sm font-medium text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-ember disabled:opacity-50"
                >
                  {nSaving ? "Speichert …" : nEditId ? "Aktualisieren" : "Notiz speichern"}
                </button>
                {nEditId && (
                  <button
                    type="button"
                    data-testid="note-cancel-edit-btn"
                    onClick={resetNoteForm}
                    className="font-sans text-sm font-medium text-smoke transition-colors hover:text-ember-deep"
                  >
                    Abbrechen
                  </button>
                )}
              </div>
            </div>
          </form>

          <div className="mt-10 border border-black/10 p-6 md:p-8" data-testid="quote-story-form">
            <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.3em] text-ember-deep">
              Zitat-Story
            </p>
            <p className="mb-6 max-w-xl font-sans text-sm font-light text-smoke">
              Ein Gedanke aus Deinem Notizbuch als Instagram-Story:
              Dein Text in Schreibmaschinenschrift — Zeilenumbrüche bleiben erhalten. Englischen Text
              dazu tippen, um beide Sprachen als Story zu bekommen.
            </p>
            <textarea
              data-testid="quote-story-input"
              maxLength={600}
              rows={3}
              value={qText}
              onChange={(e) => setQText(e.target.value)}
              placeholder="Die besten Momente lassen sich nicht planen. Ich bin da, wenn sie passieren."
              className={`${inputClass} resize-y`}
            />
            <textarea
              data-testid="quote-story-en-input"
              maxLength={600}
              rows={3}
              value={qTextEn}
              onChange={(e) => setQTextEn(e.target.value)}
              placeholder="Englische Version (optional) — erzeugt eine zweite Story"
              className={`${inputClass} mt-4 resize-y`}
            />
            <label
              className="mt-4 flex cursor-pointer items-center gap-3 font-sans text-[13px] font-medium text-smoke transition-colors hover:text-bone"
              data-testid="quote-story-publish-label"
            >
              <input
                type="checkbox"
                checked={qPublish}
                onChange={(e) => setQPublish(e.target.checked)}
                data-testid="quote-story-publish"
                className="h-3.5 w-3.5 accent-ember"
              />
              Auch als Fieldnote auf der Website veröffentlichen
            </label>
            <button
              type="button"
              data-testid="quote-story-submit"
              onClick={makeQuoteStory}
              disabled={qBusy}
              className="mt-4 inline-flex items-center gap-3 border border-ember-deep bg-ember-deep px-8 py-3.5 font-sans text-sm font-medium text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-ember disabled:opacity-50"
            >
              <Instagram size={14} />
              {qBusy ? "Wird erstellt …" : "Story-Slide erstellen"}
            </button>
          </div>

          <div className="mt-10 border border-black/10 p-6 md:p-8" data-testid="highlight-cover-form">
            <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.3em] text-ember-deep">
              Highlight-Cover
            </p>
            <p className="mb-6 max-w-xl font-sans text-sm font-light text-smoke">
              Titelbilder für Deine Instagram-Highlights als runde Stempel im Seiten-Look.
              Ein Wort eintippen (z. B. Analog, Camping, Leica) und erstellen.
            </p>
            <div className="flex flex-wrap items-end gap-4">
              <input
                data-testid="highlight-cover-input"
                maxLength={20}
                value={hlText}
                onChange={(e) => setHlText(e.target.value)}
                placeholder="z. B. Analog"
                className={`${inputClass} max-w-xs`}
              />
              <button
                type="button"
                data-testid="highlight-cover-submit"
                onClick={makeHighlight}
                disabled={hlBusy}
                className="inline-flex items-center gap-3 border border-ember-deep bg-ember-deep px-8 py-3.5 font-sans text-sm font-medium text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-ember disabled:opacity-50"
              >
                <Instagram size={14} />
                {hlBusy ? "Wird erstellt …" : "Cover erstellen"}
              </button>
            </div>
          </div>

          <div className="mt-10 space-y-4" data-testid="notes-list">
            {notes.length === 0 ? (
              <p className="font-sans text-sm font-light text-smoke" data-testid="notes-empty">
                Noch keine Notizen.
              </p>
            ) : (
              notes.map((note) => (
                <div
                  key={note.id}
                  data-testid={`note-item-${note.id}`}
                  className="flex flex-wrap items-start justify-between gap-4 border border-black/10 p-5"
                >
                  <div className="min-w-0">
                    <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
                      {note.date?.slice(0, 10).split("-").reverse().join(".")}
                      {note.location && <span className="text-ember"> · {note.location}</span>}
                      {note.images?.length > 0 && <span> · {note.images.length} Bild{note.images.length > 1 ? "er" : ""}</span>}
                      <span className={note.applause > 0 ? "text-ember" : ""} data-testid={`note-applause-${note.id}`}>
                        {" "}· {note.applause ?? 0}× Berührt mich
                      </span>
                    </p>
                    <p className="font-sans text-sm font-light leading-relaxed text-bone">{note.text}</p>
                  </div>
                  <div className="flex gap-4">
                    {note.images?.length > 0 && (
                      <button
                        type="button"
                        data-testid={`note-story-${note.id}`}
                        onClick={() => makeStoryFor(note)}
                        disabled={storyFor === note.id}
                        title="Instagram-Story-Slide (1080×1920) aus dieser Notiz erstellen"
                        className="inline-flex items-center gap-1.5 font-sans text-[12px] font-medium text-smoke transition-colors hover:text-ember disabled:opacity-50"
                      >
                        <Instagram size={13} />
                        {storyFor === note.id ? "Wird erstellt …" : "Story"}
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`note-edit-${note.id}`}
                      onClick={() => startEditNote(note)}
                      className="font-sans text-[12px] font-medium text-smoke transition-colors hover:text-ember-deep"
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      data-testid={`note-delete-${note.id}`}
                      onClick={() => removeNote(note.id)}
                      className="text-smoke transition-colors hover:text-ember-deep"
                      aria-label="Notiz löschen"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ============ ANFRAGEN ============ */}
      {tab === "anfragen" && (
        <>
          <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 border-b border-black/10 pb-5" data-testid="admin-status-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                data-testid={`admin-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`font-sans text-[13px] font-medium tracking-wide transition-colors ${
                  filter === f ? "text-ember" : "text-smoke hover:text-bone"
                }`}
              >
                {f === "alle" ? "Alle" : STATUS_LABELS[f]}
                <span className="ml-2 text-bone/40">
                  {f === "alle" ? inquiries.length : inquiries.filter((i) => i.status === f).length}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-10 space-y-4">
            {filtered.length === 0 && (
              <p className="border border-black/10 p-10 text-center font-sans text-sm text-smoke" data-testid="admin-empty-state">
                Keine Anfragen vorhanden.
              </p>
            )}
            {filtered.map((inq) => (
              <motion.article
                key={inq.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="border border-black/10 p-6 md:p-8"
                data-testid={`inquiry-card-${inq.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-baseline gap-4">
                      <h2 className="font-serif text-2xl text-bone" data-testid={`inquiry-name-${inq.id}`}>{inq.name}</h2>
                      <span className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_COLORS[inq.status] || STATUS_COLORS.neu}`}>
                        {STATUS_LABELS[inq.status] || inq.status}
                      </span>
                    </div>
                    <a
                      href={`mailto:${inq.email}`}
                      className="mt-1 inline-block font-sans text-sm text-smoke transition-colors hover:text-ember-deep"
                      data-testid={`inquiry-email-${inq.id}`}
                    >
                      {inq.email}
                    </a>
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                    {new Date(inq.created_at).toLocaleDateString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <span className="border border-black/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/80">
                    {inq.project_type}
                  </span>
                  {inq.budget && (
                    <span className="border border-black/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/80">
                      {inq.budget}
                    </span>
                  )}
                </div>
                <p className="mt-4 font-sans text-sm font-light leading-relaxed text-bone/90">{inq.message}</p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      data-testid={`inquiry-status-${inq.id}-${value}`}
                      onClick={() => setStatus(inq.id, value)}
                      className={`font-sans text-[12px] font-medium transition-colors ${
                        inq.status === value ? "text-ember" : "text-smoke hover:text-ember-deep"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid={`inquiry-delete-${inq.id}`}
                    onClick={() => remove(inq.id)}
                    className="ml-auto text-smoke transition-colors hover:text-ember-deep"
                    aria-label="Anfrage löschen"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </>
      )}

      {/* ============ STIMMEN ============ */}
      {tab === "stimmen" && (
        <div className="mt-10" data-testid="admin-stimmen-section">
          <form onSubmit={saveStimme} className="border border-black/10 p-6 md:p-8" data-testid="stimme-form">
            <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.3em] text-ember-deep">
              {sEditId ? "Kundenstimme bearbeiten" : "Neue Kundenstimme"}
            </p>
            <p className="mb-8 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
              Bis zu drei Zitate auf der Startseite. Solange keine eingetragen sind, bleibt der Abschnitt unsichtbar.
            </p>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="md:col-span-2">
                <label htmlFor="stimme-text" className={labelClass}>
                  Zitat *
                </label>
                <textarea
                  id="stimme-text"
                  data-testid="stimme-text-input"
                  required
                  minLength={2}
                  maxLength={600}
                  rows={3}
                  value={sText}
                  onChange={(e) => setSText(e.target.value)}
                  placeholder="z. B. Wim hat unser Haus so gesehen, wie wir es uns immer gewünscht haben."
                  className={`${inputClass} resize-y`}
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="stimme-text-en" className={labelClass}>
                  Zitat Englisch (optional)
                </label>
                <textarea
                  id="stimme-text-en"
                  data-testid="stimme-text-en-input"
                  maxLength={600}
                  rows={3}
                  value={sTextEn}
                  onChange={(e) => setSTextEn(e.target.value)}
                  placeholder="English version of the quote"
                  className={`${inputClass} resize-y`}
                />
              </div>
              <div>
                <label htmlFor="stimme-name" className={labelClass}>
                  Name *
                </label>
                <input
                  id="stimme-name"
                  data-testid="stimme-name-input"
                  required
                  maxLength={120}
                  value={sName}
                  onChange={(e) => setSName(e.target.value)}
                  placeholder="z. B. Tohru Nakamura"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="stimme-role" className={labelClass}>
                  Funktion (optional)
                </label>
                <input
                  id="stimme-role"
                  data-testid="stimme-role-input"
                  maxLength={120}
                  value={sRole}
                  onChange={(e) => setSRole(e.target.value)}
                  placeholder="z. B. Küchenchef"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="stimme-house" className={labelClass}>
                  Haus (optional)
                </label>
                <input
                  id="stimme-house"
                  data-testid="stimme-house-input"
                  maxLength={120}
                  value={sHouse}
                  onChange={(e) => setSHouse(e.target.value)}
                  placeholder="z. B. Tohru in der Schreiberei"
                  className={inputClass}
                />
              </div>
              <div className="flex items-end gap-4 md:col-span-2">
                <button
                  type="submit"
                  data-testid="stimme-submit-btn"
                  className="border border-ember-deep bg-ember-deep px-8 py-3 font-sans text-sm font-medium text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-ember"
                >
                  {sEditId ? "Aktualisieren" : "Stimme speichern"}
                </button>
                {sEditId && (
                  <button
                    type="button"
                    data-testid="stimme-cancel-edit-btn"
                    onClick={resetStimmeForm}
                    className="font-sans text-sm font-medium text-smoke transition-colors hover:text-ember-deep"
                  >
                    Abbrechen
                  </button>
                )}
              </div>
            </div>
          </form>

          <div className="mt-10 space-y-4">
            {stimmen.length === 0 ? (
              <p className="font-sans text-sm font-light text-smoke" data-testid="stimmen-empty">
                Noch keine Kundenstimmen eingetragen.
              </p>
            ) : (
              stimmen.map((q) => {
                const byline = [q.role, q.house].filter(Boolean).join(", ");
                return (
                  <div
                    key={q.id}
                    className="flex flex-wrap items-start justify-between gap-4 border border-black/10 p-5"
                    data-testid={`stimme-row-${q.id}`}
                  >
                    <div className="max-w-2xl">
                      <p className="font-serif text-lg font-light italic text-bone">„{q.text_de}“</p>
                      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
                        {q.name}{byline && ` · ${byline}`}
                      </p>
                    </div>
                    <div className="flex gap-5">
                      <button
                        type="button"
                        onClick={() => startEditStimme(q)}
                        data-testid={`stimme-edit-${q.id}`}
                        className="font-sans text-[12px] font-medium text-smoke transition-colors hover:text-ember-deep"
                      >
                        Bearbeiten
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteStimme(q.id)}
                        data-testid={`stimme-delete-${q.id}`}
                        className="font-sans text-[12px] font-medium text-smoke transition-colors hover:text-ember-deep"
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ============ EINSTELLUNGEN ============ */}
      {tab === "einstellungen" && (
        <div className="mt-10" data-testid="admin-einstellungen-section">
          <p className="mb-8 max-w-2xl font-sans text-sm font-light leading-relaxed text-smoke">
            Dein offizielles Logo als Datei — für Visitenkarten, E-Mail-Signatur, Rechnungen und
            alles Weitere. Außerdem das Portrait für die Über-mich-Seite.
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              { file: "logo-dark", name: "Dunkel — für helle Untergründe", bg: "bg-[#F5F2EC]" },
              { file: "logo-light", name: "Hell — für dunkle Untergründe", bg: "bg-[#1C1A17]" },
              { file: "logo-signatur-dark", name: "Signatur dunkel — E-Mail & Briefkopf", bg: "bg-[#F5F2EC]" },
              { file: "logo-signatur-light", name: "Signatur hell — für dunkle Untergründe", bg: "bg-[#1C1A17]" },
              { file: "visitenkarte", name: "Visitenkarte — PDF + Moo.com-Dateien", bg: "bg-[#F5F2EC]", pdf: true, preview: "/logo/logo-signatur-dark.svg", extra: [
                { id: "moo-front", label: "Moo Vorderseite", href: "/logo/visitenkarte-moo-vorderseite.png" },
                { id: "moo-back", label: "Moo Rückseite", href: "/logo/visitenkarte-moo-rueckseite.png" },
              ] },
              { file: "instagram-profil", name: "Instagram — Profilbild 1080 × 1080 (JPG)", bg: "bg-[#ECE8DF]", jpgOnly: true, preview: "/logo/instagram-profil.jpg" },
              { file: "sticker", name: "Sticker rund — Moo.com 51 mm, 3 Designs (PNG, mit Beschnitt)", bg: "bg-[#ECE8DF]", round: true, preview: "/logo/sticker-moo-papier.png", stickerLinks: [
                { id: "sticker-papier", label: "Papier", href: "/logo/sticker-moo-papier.png" },
                { id: "sticker-tinte", label: "Tinte", href: "/logo/sticker-moo-tinte.png" },
                { id: "sticker-ember", label: "Ember", href: "/logo/sticker-moo-ember.png" },
              ] },
              { file: "favicon", name: "Favicon — Browser-Tab & Lesezeichen", bg: "bg-[#ECE8DF]" },
            ].map((f) => (
              <div key={f.file} className="border border-black/10" data-testid={`logo-card-${f.file}`}>
                <div className={`flex h-44 items-center justify-center ${f.bg}`}>
                  <img
                    src={f.preview || `/logo/${f.file}.svg`}
                    alt={f.name}
                    className={f.round ? "h-32 w-32 rounded-full shadow-sm" : "h-20 w-auto"}
                  />
                </div>
                <div className="p-4">
                  <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">{f.name}</p>
                  <div className="flex flex-wrap gap-3">
                    {f.stickerLinks ? (
                      f.stickerLinks.map((x) => (
                        <a
                          key={x.id}
                          href={x.href}
                          download
                          data-testid={`logo-download-${x.id}`}
                          className="border border-black/20 px-4 py-2 font-sans text-[12px] font-medium text-bone transition-colors hover:border-ember hover:text-ember-deep"
                        >
                          {x.label}
                        </a>
                      ))
                    ) : f.jpgOnly ? (
                      <a
                        href={`/logo/${f.file}.jpg`}
                        download
                        data-testid={`logo-download-jpg-${f.file}`}
                        className="border border-black/20 px-4 py-2 font-sans text-[12px] font-medium text-bone transition-colors hover:border-ember hover:text-ember-deep"
                      >
                        JPG
                      </a>
                    ) : f.pdf ? (
                      <>
                        <a
                          href={`/logo/${f.file}.pdf`}
                          download
                          data-testid={`logo-download-pdf-${f.file}`}
                          className="border border-black/20 px-4 py-2 font-sans text-[12px] font-medium text-bone transition-colors hover:border-ember hover:text-ember-deep"
                        >
                          PDF
                        </a>
                        {(f.extra || []).map((x) => (
                          <a
                            key={x.id}
                            href={x.href}
                            download
                            data-testid={`logo-download-${x.id}`}
                            className="border border-black/20 px-4 py-2 font-sans text-[12px] font-medium text-bone transition-colors hover:border-ember hover:text-ember-deep"
                          >
                            {x.label}
                          </a>
                        ))}
                      </>
                    ) : (
                      <>
                        <a
                          href={`/logo/${f.file}.svg`}
                          download
                          data-testid={`logo-download-svg-${f.file}`}
                          className="border border-black/20 px-4 py-2 font-sans text-[12px] font-medium text-bone transition-colors hover:border-ember hover:text-ember-deep"
                        >
                          SVG
                        </a>
                        <a
                          href={`/logo/${f.file}.png`}
                          download
                          data-testid={`logo-download-png-${f.file}`}
                          className="border border-black/20 px-4 py-2 font-sans text-[12px] font-medium text-bone transition-colors hover:border-ember hover:text-ember-deep"
                        >
                          PNG
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={uploadPortrait} className="mt-12 border border-black/10 p-6 md:p-8" data-testid="portrait-upload-form">
            <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.3em] text-ember-deep">
              Portrait für die Über-mich-Seite
            </p>
            <p className="mb-6 max-w-xl font-sans text-sm font-light text-smoke">
              Ein Foto von dir, das auf „Über mich“ neben dem Text erscheint. JPG, PNG oder WebP, max. 10 MB.
            </p>
            <div className="flex flex-wrap items-center gap-6">
              {portrait && (
                <img
                  src={portrait}
                  alt="Aktuelles Portrait"
                  className="h-32 w-auto border border-black/10"
                  data-testid="portrait-current"
                />
              )}
              <label
                htmlFor="portrait-file"
                data-testid="portrait-file-label"
                className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-black/20 px-6 py-6 font-sans text-[13px] font-medium text-smoke transition-colors hover:border-ember hover:text-ember-deep"
              >
                <ImagePlus size={16} />
                {pFile ? pFile.name : "Portrait wählen"}
              </label>
              <input
                id="portrait-file"
                ref={portraitFileRef}
                data-testid="portrait-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => setPFile(e.target.files?.[0] || null)}
              />
              <button
                type="submit"
                data-testid="portrait-upload-submit"
                disabled={pUploading}
                className="inline-flex items-center gap-3 border border-ember-deep bg-ember-deep px-8 py-3.5 font-sans text-sm font-medium text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-ember disabled:opacity-50"
              >
                {pUploading ? "Wird hochgeladen …" : "Speichern"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ============ BILD-DETAIL ============ */}
      {editWork && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-obsidian/90 p-4"
          onClick={() => setEditWork(null)}
          data-testid="work-detail-overlay"
        >
          <div
            className="w-full max-w-md border border-black/20 bg-obsidian p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="work-detail-panel"
          >
            <div className="mb-5 flex items-center justify-between">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ember-deep">Bild bearbeiten</p>
              <button
                type="button"
                onClick={() => setEditWork(null)}
                aria-label="Schließen"
                className="text-smoke transition-colors hover:text-bone"
              >
                <X size={18} />
              </button>
            </div>
            <img src={thumbUrl(editWork.image, 800)} alt={captionFor(editWork)} className="mb-6 w-full border border-black/10 object-contain" />
            <div className="space-y-5">
              <div>
                <label className={labelClass}>Kategorie *</label>
                <select
                  value={(editWork.art || "").toLowerCase()}
                  data-testid="work-detail-art"
                  onChange={(e) => saveWorkMeta(editWork.id, { art: e.target.value })}
                  className={inputClass}
                >
                  {RUBRIKEN.filter(([v]) => v).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Name *</label>
                <input
                  defaultValue={editWork.name || editWork.title || ""}
                  data-testid="work-detail-name"
                  onBlur={(e) => saveWorkMeta(editWork.id, { name: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  placeholder="z. B. Esperit Roca"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Name auf Englisch</label>
                <input
                  defaultValue={editWork.name_en || ""}
                  data-testid="work-detail-name-en"
                  onBlur={(e) => saveWorkMeta(editWork.id, { name_en: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  placeholder="nur falls anders"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Ort</label>
                <input
                  defaultValue={editWork.ort || ""}
                  data-testid="work-detail-ort"
                  onBlur={(e) => saveWorkMeta(editWork.id, { ort: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  placeholder="z. B. Girona"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Ort auf Englisch</label>
                <input
                  defaultValue={editWork.ort_en || ""}
                  data-testid="work-detail-ort-en"
                  onBlur={(e) => saveWorkMeta(editWork.id, { ort_en: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  placeholder="nur falls anders, z. B. Brittany"
                  className={inputClass}
                />
              </div>
              <details className="border-t border-black/10 pt-4">
                <summary className="cursor-pointer font-sans text-[13px] font-medium text-smoke">Weitere Optionen</summary>
                <div className="mt-4 space-y-5">
                  <div>
                    <label className={labelClass}>Bildbeschreibung für Google (DE)</label>
                    <input
                      defaultValue={editWork.description || editWork.alt_de || ""}
                      data-testid="work-detail-desc"
                      onBlur={(e) => saveWorkMeta(editWork.id, { description: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                      placeholder="z. B. Köche am Pass"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Bildbeschreibung (EN)</label>
                    <input
                      defaultValue={editWork.description_en || editWork.alt_en || ""}
                      data-testid="work-detail-desc-en"
                      onBlur={(e) => saveWorkMeta(editWork.id, { description_en: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                      placeholder="e. g. chefs at the pass"
                      className={inputClass}
                    />
                  </div>
                  <label className="flex cursor-pointer items-center gap-2 font-sans text-[13px] font-medium text-smoke">
                    <input
                      type="checkbox"
                      data-testid="work-detail-hidden"
                      checked={Boolean(editWork.hidden)}
                      onChange={(e) => saveWorkMeta(editWork.id, { hidden: e.target.checked })}
                      className="h-4 w-4 accent-[#B5401F]"
                    />
                    Bild verstecken
                  </label>
                </div>
              </details>
              <p className="border-t border-black/10 pt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke" data-testid="work-detail-preview">
                So erscheint es: <span className="text-bone">{captionPreview(editWork)}</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
